import type { DragEvent, JSX, MouseEvent } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Center, Checkbox, Group, Loader, Menu, Modal, Radio, Select, SimpleGrid, Stack, Tabs, Text, TextInput, Textarea, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, offerAiApi, offersApi, type Constellation, type OfferSummary } from '../../api';
import { AI_MODELS, CONSTELLATION_LABELS, FEE_BASIS_LABELS, OFFER_SOURCES, OFFER_STATUS, formatDate, formatEuro, hoaiServiceOptions } from '../../format';
import { PageScroll } from '../../ui/PageScroll';
import { isNewTabClick, linkTo, openInNewTab } from '../../ui/links';
import { PartiesPanel } from './PartiesPanel';
import { ReferencesPanel } from './ReferencesPanel';
import { PlusTile } from '../../ui/PlusTile';
import { FEE_BASES, type FeeBasis, type HoaiService, defaultHoaiInput } from '@pst/shared';
import { FileDrop } from '../../ui/FileDrop';
import { useContextMenu } from '../../ui/ContextMenu';
import { type OfferRef, useOfferActions } from './OfferActions';

/**
 * Angebote (module 5): the offers of the office, newest first, with the
 * tile ＋ in front of the newest to start one. Beside it, the parties who
 * take part in offers, kept once for all of them.
 */
export function OffersPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const current = location.pathname.endsWith('/beteiligte') ? 'beteiligte' : location.pathname.endsWith('/referenzen') ? 'referenzen' : 'angebote';
  return (
    <PageScroll>
      <Stack gap="md" pb="xl">
        <Stack gap={0}>
          <Title order={3}>Angebote</Title>
          <Text size="sm" c="dimmed">Angebote vor dem Projekt: Kalkulation nach der Vorlage des Büros, Varianten der Konstellation, Angebotsunterlagen. Aus einem beauftragten Angebot wird ein Projekt.</Text>
        </Stack>
        <Tabs value={current} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="angebote" component="a" {...linkTo(navigate, '/angebote')}>Angebote</Tabs.Tab>
            <Tabs.Tab value="beteiligte" component="a" {...linkTo(navigate, '/angebote/beteiligte')}>Beteiligte</Tabs.Tab>
            <Tabs.Tab value="referenzen" component="a" {...linkTo(navigate, '/angebote/referenzen')}>Referenzen</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="angebote" pt="md"><OfferTiles /></Tabs.Panel>
          <Tabs.Panel value="beteiligte" pt="md"><PartiesPanel /></Tabs.Panel>
          <Tabs.Panel value="referenzen" pt="md"><ReferencesPanel /></Tabs.Panel>
        </Tabs>
      </Stack>
    </PageScroll>
  );
}

function OfferTiles(): JSX.Element {
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['offers'], queryFn: offersApi.list });
  const [creating, setCreating] = useState<null | 'blank' | 'documents' | 'file'>(null);
  const [dropped, setDropped] = useState<File[]>([]);
  const actions = useOfferActions();
  const context = useContextMenu<OfferSummary>((offer, close) => actions.items(offer, close));
  if (list.isPending) return <Center h={120}><Loader /></Center>;
  if (list.isError) return <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Die Angebote konnten nicht geladen werden.'}</Alert>;
  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setDropped(files);
    // A PST file is read in, everything else is evaluated as documents.
    setCreating(files.length === 1 && files[0]!.name.toLowerCase().endsWith('.json') ? 'file' : 'documents');
  };
  return (
    <>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 4 }} spacing="sm">
        <PlusTile label="Neues Angebot" hint="Klicken, oder die Unterlagen (PDF, ZIP, Word) oder eine PST-Datei (JSON) hierher ziehen" onClick={() => setCreating('blank')} onDrop={onDrop} />
        {list.data.map((o) => (
          <OfferTile key={o.id} offer={o} onOpen={(e) => (isNewTabClick(e) ? openInNewTab(`/angebote/${o.id}`) : navigate(`/angebote/${o.id}`))}
            onContextMenu={(e) => context.open(e, o)} menu={actions.items} />
        ))}
      </SimpleGrid>
      {list.data.length === 0 && <Text size="sm" c="dimmed" mt="sm">Noch keine Angebote.</Text>}
      <Group justify="flex-end" mt="sm">
        <Button size="xs" variant="subtle" color="gray" onClick={() => setCreating('file')} title="ein aus PST heruntergeladenes Angebot (JSON) einlesen – Beteiligte und Referenzen werden über den Namen zugeordnet oder neu angelegt">Angebot aus PST-Datei einlesen …</Button>
      </Group>
      {context.menu}
      {actions.dialogs}
      {creating && creating !== 'file' && <NewOfferModal start={creating} dropped={dropped} onClose={() => { setCreating(null); setDropped([]); }} />}
      {creating === 'file' && <ImportOfferModal dropped={dropped} onClose={() => { setCreating(null); setDropped([]); }} />}
    </>
  );
}

function OfferTile({ offer: o, onOpen, onContextMenu, menu }: {
  offer: OfferSummary;
  onOpen: (e: MouseEvent) => void;
  onContextMenu: (e: MouseEvent) => void;
  menu: (offer: OfferRef, close: () => void) => JSX.Element;
}): JSX.Element {
  const s = OFFER_STATUS[o.status as keyof typeof OFFER_STATUS] ?? OFFER_STATUS.entwurf;
  // Controlled so an entry in a submenu closes the ⋯ menu too.
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <Card withBorder padding="md" role="link" tabIndex={0} className="pst-tile" style={{ cursor: 'pointer' }} onClick={onOpen}
      onAuxClick={(e) => { if (e.button === 1) openInNewTab(`/angebote/${o.id}`); }}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(e as unknown as MouseEvent); }}>
      <Group justify="space-between" wrap="nowrap" gap={4}>
        <Text size="xs" c="dimmed" ff="monospace">{o.code}</Text>
        <Group gap={4} wrap="nowrap">
          <Badge size="xs" variant="light" color={s.color}>{s.label}</Badge>
          <Menu position="bottom-end" withinPortal width={240} opened={menuOpen} onChange={setMenuOpen}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`Aktionen für ${o.code}`} mt={-4} mr={-6}
                onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>{'⋯'}</ActionIcon>
            </Menu.Target>
            <Menu.Dropdown onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>{menu(o, () => setMenuOpen(false))}</Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
      <Text fw={600} lineClamp={2} mt={2}>{o.name}</Text>
      {o.client && <Text size="xs" c="dimmed" lineClamp={1}>{o.client}</Text>}
      {o.dueDate && <Text size="xs" c="dimmed">Abgabe {formatDate(o.dueDate)}</Text>}
      <Stack gap={0} mt={6}>
        {o.variants.map((v) => (
          <Text key={v.id} size="xs"><Text span ff="monospace" c="dimmed">{v.code}</Text> {v.name}: <Text span fw={600}>{v.net ? formatEuro(v.net) : '—'}</Text> netto</Text>
        ))}
      </Stack>
    </Card>
  );
}

const ACCEPT = '.pdf,.zip,.docx,.xlsx,.txt,.md,.csv,.png,.jpg,.jpeg';

/**
 * A new offer, in two questions (AP-111): where its details come from
 * (documents or a description, which the AI evaluates once created, or none)
 * and how the fee is found (by hours, after the HOAI, or both). The
 * constellation is asked for first, as the office's rules want it settled
 * before the evaluation.
 */
function NewOfferModal({ start, dropped, onClose }: { start: 'blank' | 'documents'; dropped: File[]; onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<keyof typeof OFFER_SOURCES>(start === 'documents' ? 'documents' : 'none');
  const [feeBasis, setFeeBasis] = useState<FeeBasis>('hours');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [constellation, setConstellation] = useState<Constellation>('einzel');
  const [files, setFiles] = useState<File[]>(dropped.filter((f) => ACCEPT.split(',').some((x) => f.name.toLowerCase().endsWith(x))));
  const [description, setDescription] = useState('');
  const [evaluate, setEvaluate] = useState(true);
  const [service, setService] = useState<HoaiService>('gebaeude');
  const ai = useQuery({ queryKey: ['offer-ai-status'], queryFn: offerAiApi.status });
  const available = ai.data?.available === true;
  const withHoai = feeBasis !== 'hours';
  const create = useMutation({
    mutationFn: async () => {
      const { id } = await offersApi.create({ code, name, client, constellation, feeBasis });
      if (source === 'documents') for (const f of files) await offerAiApi.addDocument(id, f);
      if (source === 'text') await offerAiApi.addText(id, 'Beschreibung.txt', description);
      if (source !== 'none' && evaluate && available) await offerAiApi.analyse(id, '');
      if (source === 'none' && withHoai) {
        // An empty fee calculation to fill in; with documents the evaluation proposes them.
        const off = await offersApi.get(id);
        await offersApi.update(id, off.version, { hoai: [{ label: name, input: defaultHoaiInput(service), building: service === 'gebaeude' || service === 'innenraeume' ? { kg300: 0, kg400: 0, plansKg400: false } : null, points: null }] });
      }
      return id;
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: ['offers'] });
      navigate(`/angebote/${id}${source !== 'none' ? '/auswertung' : withHoai ? '/hoai' : ''}`);
    },
  });
  const inputOk = source === 'none' || (source === 'documents' ? files.length > 0 : description.trim().length >= 20);
  const codeOk = /^[A-Z0-9]{2,6}$/.test(code);
  return (
    <Modal opened onClose={onClose} title="Neues Angebot" size="lg">
      <Stack gap="sm">
        <Radio.Group label="Woher kommen die Angaben?" value={source} onChange={(v) => setSource(v as keyof typeof OFFER_SOURCES)}>
          <Stack gap={6} mt={4}>
            {(Object.keys(OFFER_SOURCES) as (keyof typeof OFFER_SOURCES)[]).map((k) => (
              <Radio key={k} value={k} label={OFFER_SOURCES[k].label} description={OFFER_SOURCES[k].hint} />
            ))}
          </Stack>
        </Radio.Group>
        {source === 'documents' && (
          <Stack gap={4}>
            <FileDrop accept={ACCEPT} multiple label="Unterlagen hierher ziehen oder auswählen" hint="Auslobung, Leistungsbeschreibung, Anlagen – PDF, ZIP, Word, Excel, Text" onFiles={(f) => setFiles((old) => [...old, ...f])} />
            {files.length > 0 && <Text size="xs">{files.map((f) => f.name).join(', ')} <Anchor size="xs" component="button" onClick={() => setFiles([])}>leeren</Anchor></Text>}
          </Stack>
        )}
        {source === 'text' && (
          <Textarea label="Beschreibung der Aufgabe" autosize minRows={4} maxRows={14} value={description} onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder="z. B. die Anfrage aus der E-Mail: Vorhaben, gewünschte Leistungen, Termine, Budget …" />
        )}
        <Radio.Group label="Wie wird das Honorar ermittelt?" value={feeBasis} onChange={(v) => setFeeBasis(v as FeeBasis)}>
          <Stack gap={6} mt={4}>
            {FEE_BASES.map((k) => <Radio key={k} value={k} label={FEE_BASIS_LABELS[k].label} description={FEE_BASIS_LABELS[k].hint} />)}
          </Stack>
        </Radio.Group>
        {withHoai && source === 'none' && (
          <Select label="Leistungsbild" description="Bezugsgröße, Honorarzone und Leistungsphasen im Reiter HOAI" value={service} onChange={(v) => v && setService(v as HoaiService)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
            data={hoaiServiceOptions()} />
        )}
        {withHoai && source !== 'none' && <Text size="xs" c="dimmed">Die KI liest Leistungsbild, Leistungsphasen, Bezugsgröße und Honorarzone mit aus, soweit die Unterlagen sie nennen; beim Übernehmen entstehen daraus die HOAI-Ermittlungen.</Text>}
        {source !== 'none' && (
          available
            ? <Checkbox checked={evaluate} onChange={(e) => setEvaluate(e.currentTarget.checked)} label="gleich auswerten lassen" description={`Die KI liest die Unterlagen (${AI_MODELS[ai.data?.model as keyof typeof AI_MODELS] ?? ai.data?.model ?? ''}); das dauert einige Minuten. Das Ergebnis erscheint im Angebot unter „Auswertung“ und wird erst nach Ihrer Prüfung verwendet.`} />
            : <Alert color="orange" py={6}>Die KI-Auswertung ist gerade nicht erreichbar. Das Angebot wird mit den Unterlagen angelegt; die Auswertung lässt sich später unter „Auswertung“ anfordern.</Alert>
        )}
        <Group gap="sm" align="flex-end" grow>
          <TextInput label="Kürzel" description="2–6 Großbuchstaben, für die Dateinamen" value={code} onChange={(e) => setCode(e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={6} error={code && !codeOk ? 'mindestens 2 Zeichen' : undefined} data-autofocus />
          <Select label="Konstellation" data={Object.entries(CONSTELLATION_LABELS).map(([value, label]) => ({ value, label }))} value={constellation} onChange={(v) => v && setConstellation(v as Constellation)} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        </Group>
        <TextInput label="Vorhaben" placeholder="z. B. Freiraumplanung Schulhof, Musterstadt" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label="Auftraggeber" value={client} onChange={(e) => setClient(e.currentTarget.value)} />
        <Text size="xs" c="dimmed">Die Beteiligten (wer bietet, wer ist Nachunternehmer) werden im Angebot gewählt; weitere Konstellationen lassen sich dort als Varianten gegenüberstellen.</Text>
        {create.isError && <Alert color="red" py={6}>{create.error instanceof ApiError ? create.error.message : 'Anlegen fehlgeschlagen.'}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!inputOk || !codeOk || !name.trim()}>Anlegen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}


/** An offer downloaded from PST (JSON) read in again; parties and references matched by name. */
function ImportOfferModal({ dropped, onClose }: { dropped: File[]; onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pstFile, setPstFile] = useState<File | null>(dropped.find((f) => f.name.toLowerCase().endsWith('.json')) ?? null);
  const readIn = useMutation({
    mutationFn: async () => {
      let doc: unknown;
      try { doc = JSON.parse(await pstFile!.text()); } catch { throw new Error('Die Datei ist kein gültiges JSON.'); }
      return offersApi.importFile(doc);
    },
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ['offers'] });
      await queryClient.invalidateQueries({ queryKey: ['offer-parties'] });
      await queryClient.invalidateQueries({ queryKey: ['offer-references'] });
      if (r.notes.length === 0) navigate(`/angebote/${r.id}`);
    },
  });
  return (
    <Modal opened onClose={onClose} title="Angebot aus PST-Datei einlesen" size="lg">
      <Stack gap={4}>
        <FileDrop accept=".json" label="PST-Datei hierher ziehen oder auswählen" hint="Angebot als Datei (JSON), heruntergeladen aus PST über „Herunterladen“" onFiles={(f) => setPstFile(f[0] ?? null)} />
        {pstFile && <Text size="xs">{pstFile.name}</Text>}
        <Text size="xs" c="dimmed">Kürzel, Vorhaben, Varianten, Texte und HOAI kommen aus der Datei; ist das Kürzel vergeben, wird eine Ziffer angehängt. Bankverbindung, Steuernummer und Geburtsdatum der Beteiligten enthält die Datei nicht.</Text>
        {readIn.isError && <Alert color="red" py={6}>{readIn.error instanceof Error ? readIn.error.message : 'Einlesen fehlgeschlagen.'}</Alert>}
        {readIn.data && readIn.data.notes.length > 0 && (
          <Alert color="brand" py={6} title={`Angebot ${readIn.data.code} eingelesen`}>
            <Stack gap={2}>{readIn.data.notes.map((n) => <Text key={n} size="xs">{n}</Text>)}</Stack>
          </Alert>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>{readIn.data ? 'Schließen' : 'Abbrechen'}</Button>
          {readIn.data
            ? <Button onClick={() => navigate(`/angebote/${readIn.data.id}`)}>Zum Angebot</Button>
            : <Button onClick={() => readIn.mutate()} loading={readIn.isPending} disabled={!pstFile}>Einlesen</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}
